// Copyright (c) 2020-2021, 2023 Visual Intelligence LP. All rights reserved.
//
import * as React from 'react';
import { withStyles } from "tss-react/mui";
import { FORMAT_LABELS } from '@shared/constants/file';
import { parseBytes } from '@shared/utils/common';
import Importcon from '@shared/icons/Import';
import FileUpload, { FileUploadProps } from '@shared/components/FileUpload';
import Button from '@shared/components/Button';
import Flex from '@shared/components/Flex';
import { alpha } from '@mui/material';

// Prop interface for the main class.
export interface InitialStateProps {
  className?: string;
  uploadProps: Omit<FileUploadProps, 'onUpload' | 'classes'>;
  onValidate: (file: File) => any;
}

// Class to display a panel to browse to and load an import file.
class InitialState extends React.Component<InitialStateProps> {

  // Build the label to display above the browse button.
  private get initialStateText() {
    const { uploadProps } = this.props;
    const { maxSize, accept } = uploadProps.config;
    const { amount, unit } = parseBytes(maxSize ?? 0);
    let acceptLabels = '';
    if (accept) {
      acceptLabels = Object.values(accept).map((type) => type.join(', ')).join(', ');
    }

    return `File format: ${acceptLabels}; Maximum size: ${amount}${unit}.`;
  }

  render() {
    const { uploadProps, onValidate } = this.props;
    const classes = withStyles.getClasses(this.props);

    //  Return column consists of an icon, a label, and a button.
    return (
      <Flex
        direction="column"
        justifyContent="center"
        alignItems="center"
        className={classes.root}>

        <Importcon classes={{ root: classes.importIcon }} />

        <div className={classes.initialStateText}>{this.initialStateText}</div>

        <Button
          color="primary"
          variant="outlined"
          size="small"
          className={classes.importBtn}
        >
          <FileUpload
            {...uploadProps}
            placeholder="Browse & import"
            classes={{
              root: classes.fileUploadRoot,
              placeholder: classes.fileUploadPlaceholder
            }}
            onUpload={onValidate}
          />
        </Button>
      </Flex>
    );
  }
}

const InitialStateStyled = withStyles(
  InitialState, 
  (theme, props) => ({
    "root": {
      height: '100%',
    },
    "importIcon": {
      fontSize: 48,
      marginBottom: theme.spacing(5),
    },
    "initialStateText": {
      padding: theme.spacing(2, 4),
      borderRadius: '4px',
      background: alpha(theme.palette.colors.greyLight3, 0.08),
      color: theme.palette.colors.greyDark,
      border: `1px dashed ${theme.palette.colors.greyLight};`,
      fontSize: 11,
      marginBottom: theme.spacing(6),
    },
    "importBtn": {
      position: 'relative',
      width: 167,
      height: 48,
      overflow: 'hidden',
      border: 'none',
    },
    "fileUploadRoot": {
      position: 'absolute',
      width: '100%',
      height: '100%',
      backgroundColor: theme.palette.colors.primaryAccent,
      border: 'none',
    },
    "fileUploadPlaceholder": {
      color: theme.palette.colors.white,
      fontWeight: 500,
      fontSize: 16,
    }
  })
);

export default InitialStateStyled;